// ========================================
// COMPONENTE FILESVIEW (VISTA DE ARCHIVOS)
// Muestra los archivos de una carpeta con filtros, búsqueda y diferentes vistas
// ========================================

import React, { useState, useEffect, useRef, useMemo } from 'react';
import Swal from 'sweetalert2';
import config from '../Componentes/config';
import { useCookies } from 'react-cookie';
import { registrarLog } from './logService';

import {
    useReactTable,
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    getFilteredRowModel,
    flexRender,
} from '@tanstack/react-table';

const FilesView = ({ folder, onError, onSuccess }) => {
    // ========================================
    // ESTADOS DEL COMPONENTE
    // ========================================
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    const [hoveredFile, setHoveredFile] = useState(null);
    const dateInputRef = useRef(null);
    const [fechaArchivo, setFechaArchivo] = useState("");

    // Estados para el modal de fecha de archivo
    const [showDateModal, setShowDateModal] = useState(false);
    const [pendingFiles, setPendingFiles] = useState([]);
    const [tempFechaArchivo, setTempFechaArchivo] = useState("");

    // Estados para filtros y vista
    const [viewMode, setViewMode] = useState("grid");
    const [searchTerm, setSearchTerm] = useState("");
    const [sortBy, setSortBy] = useState("");
    const [sortOrder, setSortOrder] = useState("asc");
    const [filterType, setFilterType] = useState("");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [showDateFilter, setShowDateFilter] = useState(false);
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize] = useState(10);

    const fileInputRef = useRef(null);
    const [cookies] = useCookies(["selectedCard"]);
    const USUARIO_ID = cookies.usuario; // Usuario local para desarrollo

    // Permisos efectivos sobre la carpeta actual (resueltos en el backend)
    const puedeSubir = !!folder?.puedeSubir;
    const puedeEliminar = !!folder?.puedeEliminar;

    // ========================================
    // FUNCIONES UTILITARIAS
    // ========================================
    const getFileIcon = (mimeType) => {
        if (!mimeType) return '📄';
        if (mimeType.startsWith('image/')) return '🖼️';
        if (mimeType.startsWith('video/')) return '🎥';
        if (mimeType.startsWith('audio/')) return '🎵';
        if (mimeType.includes('pdf')) return '📕';
        if (mimeType.includes('word')) return '📘';
        if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return '📊';
        if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) return '📋';
        if (mimeType.includes('zip') || mimeType.includes('rar')) return '🗜️';
        return '📄';
    };

    const getFileType = (mimeType) => {
        if (!mimeType) return 'other';
        if (mimeType.includes('word')) return 'word';
        if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) return 'powerpoint';
        if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return 'excel';
        if (mimeType.includes('pdf')) return 'pdf';
        if (mimeType.includes('zip') || mimeType.includes('rar') || mimeType.includes('compress')) return 'zip';
        return 'other';
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('es-ES', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // ========================================
    // EFECTOS Y FILTRADO
    // ========================================
    useEffect(() => {
        loadFiles();
    }, [folder]);

    const filteredAndSortedFiles = useMemo(() => {
        let result = [...files];

        if (searchTerm) {
            result = result.filter(file =>
                file.nombreArchivo.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        if (filterType) {
            result = result.filter(file => getFileType(file.tipoMime) === filterType);
        }

        // Filtro por rango de fechas
        if (dateFrom || dateTo) {
            result = result.filter(file => {
                const fileDate = new Date(file.fechaSubida);
                const fromDate = dateFrom ? new Date(dateFrom) : null;
                const toDate = dateTo ? new Date(dateTo + 'T23:59:59') : null; // Incluir todo el día

                if (fromDate && toDate) {
                    return fileDate >= fromDate && fileDate <= toDate;
                } else if (fromDate) {
                    return fileDate >= fromDate;
                } else if (toDate) {
                    return fileDate <= toDate;
                }
                return true;
            });
        }

        if (sortBy) {
            result.sort((a, b) => {
                let valueA, valueB;
                switch (sortBy) {
                    case 'name':
                        valueA = a.nombreArchivo.toLowerCase();
                        valueB = b.nombreArchivo.toLowerCase();
                        break;
                    case 'size':
                        valueA = a.tamano || 0;
                        valueB = b.tamano || 0;
                        break;
                    case 'date':
                        valueA = new Date(a.fechaSubida);
                        valueB = new Date(b.fechaSubida);
                        break;
                    case 'type':
                        valueA = getFileType(a.tipoMime);
                        valueB = getFileType(b.tipoMime);
                        break;
                    default:
                        return 0;
                }

                if (sortOrder === 'desc') {
                    return valueA < valueB ? 1 : valueA > valueB ? -1 : 0;
                } else {
                    return valueA > valueB ? 1 : valueA < valueB ? -1 : 0;
                }
            });
        }

        return result;
    }, [files, searchTerm, sortBy, sortOrder, filterType, dateFrom, dateTo]);

    // Implementación de paginación para grid
    const paginatedFiles = useMemo(() => {
        const startIndex = currentPage * pageSize;
        const endIndex = startIndex + pageSize;
        return filteredAndSortedFiles.slice(startIndex, endIndex);
    }, [filteredAndSortedFiles, currentPage, pageSize]);

    const totalPages = Math.ceil(filteredAndSortedFiles.length / pageSize);

    // Configuración de React Table para vista de lista
    const columns = useMemo(() => [
        {
            accessorKey: 'tipoMime',
            header: '',
            cell: ({ getValue }) => (
                <div className="file-list-icon2">
                    {getFileIcon(getValue())}
                </div>
            ),
            size: 60,
            enableSorting: false,
        },
        {
            accessorKey: 'nombreArchivo',
            header: 'Nombre',
            cell: ({ getValue, row }) => (
                <div className="file-list-name" title={getValue()}>
                    {getValue()}
                </div>
            ),
        },
        {
            accessorKey: 'Usuario',
            header: 'Usuario',
            cell: ({ getValue }) => (
                <div className="file-list-name">{getValue()}</div>
            ),
            size: 120,
        },
        {
            accessorKey: 'FechaArchivo',
            header: 'Fecha del archivo',
            cell: ({ getValue }) => (
                <div className="file-list-date">
                    {getValue() || 'N/A'}
                </div>
            ),
            size: 150,
        },
        {
            accessorKey: 'fechaSubida',
            header: 'Fecha subida',
            cell: ({ getValue }) => (
                <div className="file-list-date">
                    {formatDate(getValue())}
                </div>
            ),
            size: 180,
        },
        {
            id: 'actions',
            header: 'Acciones',
            cell: ({ row }) => (
                <div className="file-list-actions">
                    <button
                        className="list-action-btn view"
                        onClick={() => handleViewFile(row.original)}
                        title="Ver archivo"
                    >
                        👁️
                    </button>
                    <button
                        className="list-action-btn download"
                        onClick={() => handleDownloadFile(row.original)}
                        title="Descargar archivo"
                    >
                        ⬇️
                    </button>
                    {puedeSubir && (
                        <button
                            className="list-action-btn rename"
                            onClick={() => handleRenameFile(row.original)}
                            title="Renombrar archivo"
                        >
                            ✏️
                        </button>
                    )}
                    {puedeEliminar && (
                        <button
                            className="list-action-btn delete"
                            onClick={() => handleDeleteFile(row.original)}
                            title="Eliminar archivo"
                        >
                            🗑️
                        </button>
                    )}
                </div>
            ),
            size: 140,
            enableSorting: false,
        },
    ], [puedeSubir, puedeEliminar]);

    const table = useReactTable({
        data: filteredAndSortedFiles,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        initialState: {
            pagination: {
                pageSize: pageSize,
            },
        },
    });

    const loadFiles = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${config.API_URL}/archivos/${folder.idCarpeta}`);
            if (response.ok) {
                const data = await response.json();
                setFiles(data);
            } else {
                onError('Error al cargar los archivos');
            }
        } catch (err) {
            onError('Error de conexión con el servidor');
        } finally {
            setLoading(false);
        }
    };

    // ========================================
    // FUNCIONES DE CONTROL
    // ========================================
    const handleSort = (field) => {
        if (sortBy === field) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(field);
            setSortOrder('asc');
        }

        // Mostrar filtro de fechas cuando se selecciona ordenar por fecha
        if (field === 'date') {
            setShowDateFilter(true);
        } else {
            setShowDateFilter(false);
            // Limpiar filtros de fecha si se cambia a otro tipo de orden
            setDateFrom('');
            setDateTo('');
        }
    };

    const clearFilters = () => {
        setSearchTerm('');
        setSortBy('');
        setSortOrder('asc');
        setFilterType('');
        setDateFrom('');
        setDateTo('');
        setShowDateFilter(false);
    };

    // Funciones de paginación
    const goToPage = (page) => {
        setCurrentPage(page);
    };

    const nextPage = () => {
        if (currentPage < totalPages - 1) {
            setCurrentPage(currentPage + 1);
        }
    };

    const previousPage = () => {
        if (currentPage > 0) {
            setCurrentPage(currentPage - 1);
        }
    };

    // Reset página cuando cambian los filtros
    useEffect(() => {
        setCurrentPage(0);
    }, [searchTerm, sortBy, filterType, dateFrom, dateTo]);

    // ========================================
    // FUNCIONES DE SUBIDA
    // ========================================
    const uploadFile = async (file, fechaEspecifica = null) => {
        const formData = new FormData();
        formData.append('archivo', file);
        formData.append('usuario', USUARIO_ID);
        formData.append('fechaArchivo', fechaEspecifica || fechaArchivo || new Date().toISOString().split('T')[0]);

        try {
            const response = await fetch(`${config.API_URL}/archivos/${folder.idCarpeta}`, {
                method: 'POST',
                body: formData
            });

            if (response.ok) {
                // No mostrar mensaje individual aquí, se mostrará el resumen al final
                return { success: true, fileName: file.name };
            } else {
                const errorData = await response.json();
                return { success: false, fileName: file.name, error: errorData.error || 'Error al subir el archivo' };
            }
        } catch (err) {
            return { success: false, fileName: file.name, error: 'Error de conexión con el servidor' };
        }
    };

    const handleFileSelect = async (event) => {
        const selectedFiles = Array.from(event.target.files);
        if (selectedFiles.length === 0) return;

        // Mostrar modal para pedir fecha antes de subir
        setPendingFiles(selectedFiles);
        setTempFechaArchivo(new Date().toISOString().split('T')[0]); // Fecha por defecto: hoy
        setShowDateModal(true);

        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setDragOver(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setDragOver(false);
    };

    const handleDrop = async (e) => {
        e.preventDefault();
        setDragOver(false);

        const droppedFiles = Array.from(e.dataTransfer.files);
        if (droppedFiles.length === 0) return;

        // Mostrar modal para pedir fecha antes de subir
        setPendingFiles(droppedFiles);
        setTempFechaArchivo(new Date().toISOString().split('T')[0]); // Fecha por defecto: hoy
        setShowDateModal(true);
    };

    // ========================================
    // FUNCIONES DE GESTIÓN DE ARCHIVOS
    // ========================================

    // Función para confirmar la fecha y proceder con la subida
    const handleConfirmDateAndUpload = async () => {
        if (!tempFechaArchivo) {
            alert('Por favor selecciona una fecha para el archivo');
            return;
        }

        setShowDateModal(false);
        setUploading(true);

        // Subir todos los archivos y recopilar resultados
        const results = [];
        for (const file of pendingFiles) {
            const result = await uploadFile(file, tempFechaArchivo);
            results.push(result);
        }

        setUploading(false);

        // Recargar archivos una sola vez al final
        loadFiles();

        // Crear resumen de resultados
        const successful = results.filter(r => r.success);
        const failed = results.filter(r => !r.success);

        if (successful.length > 0) {
            // Registrar archivos subidos exitosamente en log
            const fileNames = successful.map(r => r.fileName).join(', ');
            await registrarLog({
                Usuario: USUARIO_ID,
                Modulo: 'CarpetaAdministrativa',
                Detalle: `Archivo(s) subido(s) en carpeta ${folder.nombreCarpeta}: ${fileNames}`,
                Estado: 'Éxito',
            });

            let successMessage = '';
            if (successful.length === 1) {
                successMessage = `Archivo "${successful[0].fileName}" subido exitosamente`;
            } else if (successful.length <= 3) {
                const fileNamesDisplay = successful.map(r => `"${r.fileName}"`).join(', ');
                successMessage = `${successful.length} archivos subidos exitosamente: ${fileNamesDisplay}`;
            } else {
                successMessage = `${successful.length} archivos subidos exitosamente`;
            }
            onSuccess(successMessage);
        }

        // Mostrar errores si los hay
        if (failed.length > 0) {
            // Registrar errores en log
            const failedNames = failed.map(r => r.fileName).join(', ');
            await registrarLog({
                Usuario: USUARIO_ID,
                Modulo: 'CarpetaAdministrativa',
                Detalle: `Error al subir archivo(s) en carpeta ${folder.nombreCarpeta}: ${failedNames}`,
                Estado: 'Error',
            });

            let errorMessage = '';
            if (failed.length === 1) {
                errorMessage = `Error al subir "${failed[0].fileName}": ${failed[0].error}`;
            } else {
                errorMessage = `Error al subir ${failed.length} archivo(s)`;
            }
            onError(errorMessage);
        }

        setPendingFiles([]);
        setTempFechaArchivo("");
    };

    // Función para cancelar la subida
    const handleCancelUpload = () => {
        setShowDateModal(false);
        setPendingFiles([]);
        setTempFechaArchivo("");
    };

    const handleDeleteFile = async (file) => {
        const result = await Swal.fire({
            icon: 'warning',
            title: '¿Eliminar archivo?',
            text: `¿Estás seguro de que quieres eliminar "${file.nombreArchivo}"? Esta acción no se puede deshacer.`,
            showCancelButton: true,
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            reverseButtons: true
        });

        if (!result.isConfirmed) return;

        try {
            const response = await fetch(`${config.API_URL}/archivos/${file.idArchivo}?usuario=${encodeURIComponent(USUARIO_ID)}`, {
                method: 'DELETE'
            });

            if (response.ok) {
                // Registrar eliminación en log
                await registrarLog({
                    Usuario: USUARIO_ID,
                    Modulo: 'CarpetaAdministrativa',
                    Detalle: `Archivo eliminado: ${file.nombreArchivo} de carpeta ${folder.nombreCarpeta}`,
                    Estado: 'Éxito',
                });

                await Swal.fire({
                    icon: 'success',
                    title: '¡Archivo eliminado!',
                    text: 'El archivo se eliminó exitosamente',
                    showConfirmButton: false,
                    timer: 2000,
                    timerProgressBar: true
                });
                loadFiles();
            } else {
                const errorData = await response.json();

                // Registrar error en log
                await registrarLog({
                    Usuario: USUARIO_ID,
                    Modulo: 'CarpetaAdministrativa',
                    Detalle: `Error al eliminar archivo ${file.nombreArchivo} - ${errorData.error || 'Error desconocido'}`,
                    Estado: 'Error',
                });

                onError(errorData.error || 'Error al eliminar el archivo');
            }
        } catch (err) {
            // Registrar error de conexión en log
            await registrarLog({
                Usuario: USUARIO_ID,
                Modulo: 'CarpetaAdministrativa',
                Detalle: `Error de conexión al eliminar archivo ${file.nombreArchivo} - ${err.message}`,
                Estado: 'Error',
            });

            onError('Error de conexión con el servidor');
        }
    };

    const handleDownloadFile = async (file) => {
        const result = await Swal.fire({
            icon: 'question',
            title: '¿Descargar archivo?',
            text: `¿Quieres descargar "${file.nombreArchivo}"?`,
            showCancelButton: true,
            confirmButtonText: 'Sí, descargar',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#6c757d'
        });

        if (!result.isConfirmed) return;

        try {
            const link = document.createElement('a');
            link.href = `${config.API_URL}/archivos/download/${file.idArchivo}`;
            link.download = file.nombreArchivo;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            await Swal.fire({
                icon: 'success',
                title: '¡Descarga iniciada!',
                text: 'El archivo se está descargando',
                showConfirmButton: false,
                timer: 2000,
                timerProgressBar: true
            });
        } catch (err) {
            onError('Error al descargar el archivo');
        }
    };

    const handleViewFile = (file) => {
        window.open(`${config.API_URL}/archivos/view/${file.idArchivo}`, '_blank');
    };

    const handleRenameFile = async (file) => {
        const { value: nuevoNombre } = await Swal.fire({
            title: 'Renombrar archivo',
            input: 'text',
            inputLabel: 'Nuevo nombre del archivo',
            inputValue: file.nombreArchivo,
            showCancelButton: true,
            confirmButtonText: 'Renombrar',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#6c757d',
            inputValidator: (value) => {
                if (!value) {
                    return 'Debes ingresar un nombre para el archivo';
                }
                if (value.length > 255) {
                    return 'El nombre es demasiado largo';
                }
            }
        });

        if (!nuevoNombre) return;

        try {
            const response = await fetch(`${config.API_URL}/archivos/${file.idArchivo}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ nuevoNombre })
            });

            if (response.ok) {
                await Swal.fire({
                    icon: 'success',
                    title: '¡Archivo renombrado!',
                    text: 'El nombre del archivo se cambió exitosamente',
                    showConfirmButton: false,
                    timer: 2000,
                    timerProgressBar: true
                });
                loadFiles();
            } else {
                const errorData = await response.json();
                onError(errorData.error || 'Error al renombrar el archivo');
            }
        } catch (err) {
            onError('Error de conexión con el servidor');
        }
    };

    // ========================================
    // FUNCIONES DE RENDERIZADO
    // ========================================
    const renderControls = () => (
        <div className="files-controls">
            <div className="controls-row">
                <div className="search-container">
                    <span className="search-icon">🔍</span>
                    <input
                        type="text"
                        className="search-input"
                        placeholder="Buscar archivos por nombre..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                <div className="view-toggle">
                    <button
                        className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
                        onClick={() => setViewMode('grid')}
                    >
                        <span>⊞</span> Cuadrícula
                    </button>
                    <button
                        className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
                        onClick={() => setViewMode('list')}
                    >
                        <span>☰</span> Lista
                    </button>
                </div>
            </div>

            <div className="filters-row22">
                <select
                    className="filter-select2"
                    value={sortBy}
                    onChange={(e) => {
                        setSortBy(e.target.value);
                        if (e.target.value) setSortOrder('asc');

                        // Mostrar filtro de fechas solo cuando se selecciona fecha
                        if (e.target.value === 'date') {
                            setShowDateFilter(true);
                        } else {
                            setShowDateFilter(false);
                            setDateFrom('');
                            setDateTo('');
                        }
                    }}
                >
                    <option value="">Ordenar por...</option>
                    <option value="name">Nombre</option>
                    <option value="date">Fecha</option>
                    <option value="type">Tipo</option>
                    <option value="size">Tamaño</option>
                </select>

                {sortBy && (
                    <select
                        className="filter-select2"
                        value={sortOrder}
                        onChange={(e) => setSortOrder(e.target.value)}
                    >
                        <option value="asc">
                            {sortBy === 'name' ? 'A → Z' :
                                sortBy === 'date' ? 'Más antiguo primero' :
                                    sortBy === 'size' ? 'Menor a mayor' :
                                        sortBy === 'type' ? 'Excel → PDF → PowerPoint → Word → ZIP' : 'Ascendente'}
                        </option>
                        <option value="desc">
                            {sortBy === 'name' ? 'Z → A' :
                                sortBy === 'date' ? 'Más reciente primero' :
                                    sortBy === 'size' ? 'Mayor a menor' :
                                        sortBy === 'type' ? 'ZIP → Word → PowerPoint → PDF → Excel' : 'Descendente'}
                        </option>
                    </select>
                )}

                <select
                    className="filter-select2"
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                >
                    <option value="">Todos los tipos</option>
                    <option value="word">📘 Word</option>
                    <option value="powerpoint">📋 PowerPoint</option>
                    <option value="excel">📊 Excel</option>
                    <option value="pdf">📕 PDF</option>
                    <option value="zip">🗜️ Comprimidos</option>
                </select>

                {/* Filtros de fecha - aparecen cuando se selecciona ordenar por fecha */}
                {showDateFilter && (
                    <>
                        <div className="date-filter-group2">
                            <label className="date-label2">Desde:</label>
                            <input
                                type="date"
                                className="date-input2"
                                value={dateFrom}
                                onChange={(e) => setDateFrom(e.target.value)}
                                title="Fecha desde"
                            />
                        </div>

                        <div className="date-filter-group2">
                            <label className="date-label2">Hasta:</label>
                            <input
                                type="date"
                                className="date-input2"
                                value={dateTo}
                                onChange={(e) => setDateTo(e.target.value)}
                                title="Fecha hasta"
                            />
                        </div>
                    </>
                )}

                {(searchTerm || sortBy || filterType || dateFrom || dateTo) && (
                    <button
                        className="clear-filters-btn"
                        onClick={clearFilters}
                    >
                        ✕ Limpiar filtros
                    </button>
                )}
            </div>

            {(searchTerm || sortBy || filterType) && (
                <div className="filters-indicator">
                    {searchTerm && (
                        <span className="filter-tag">
                            Búsqueda: "{searchTerm}"
                            <span className="remove-filter" onClick={() => setSearchTerm('')}>×</span>
                        </span>
                    )}
                    {sortBy && (
                        <span className="filter-tag">
                            Orden: {
                                sortBy === 'name' ? `Nombre (${sortOrder === 'asc' ? 'A→Z' : 'Z→A'})` :
                                    sortBy === 'date' ? `Fecha (${sortOrder === 'asc' ? 'Antiguo→Reciente' : 'Reciente→Antiguo'})` :
                                        sortBy === 'size' ? `Tamaño (${sortOrder === 'asc' ? 'Menor→Mayor' : 'Mayor→Menor'})` :
                                            sortBy === 'type' ? `Tipo (${sortOrder === 'asc' ? 'Excel→ZIP' : 'ZIP→Excel'})` :
                                                `${sortBy} (${sortOrder === 'asc' ? '↑' : '↓'})`
                            }
                            <span className="remove-filter" onClick={() => setSortBy('')}>×</span>
                        </span>
                    )}
                    {filterType && (
                        <span className="filter-tag">
                            Tipo: {filterType}
                            <span className="remove-filter" onClick={() => setFilterType('')}>×</span>
                        </span>
                    )}
                    {dateFrom && (
                        <span className="filter-tag">
                            Desde: {new Date(dateFrom).toLocaleDateString('es-ES')}
                            <span className="remove-filter" onClick={() => setDateFrom('')}>×</span>
                        </span>
                    )}
                    {dateTo && (
                        <span className="filter-tag">
                            Hasta: {new Date(dateTo).toLocaleDateString('es-ES')}
                            <span className="remove-filter" onClick={() => setDateTo('')}>×</span>
                        </span>
                    )}
                </div>
            )}
        </div>
    );

    const renderGridView = () => (
        <div>
            <div className="files-grid">
                {paginatedFiles.map((file) => (
                    <div
                        key={file.idArchivo}
                        className={`file-item ${hoveredFile === file.idArchivo ? 'hovered' : ''}`}
                        onMouseEnter={() => setHoveredFile(file.idArchivo)}
                        onMouseLeave={() => setHoveredFile(null)}
                        onClick={() => handleDownloadFile(file)}
                        style={{ cursor: 'pointer' }}
                        title={`Click para descargar: ${file.nombreArchivo}`}
                    >
                        <div className="file-icon">
                            {getFileIcon(file.tipoMime)}
                        </div>
                        <div className="file-name">{file.nombreArchivo}</div>

                        {hoveredFile === file.idArchivo && (
                            <div className="file-actions">
                                <button
                                    className="action-btn view-btn"
                                    onClick={(e) => {
                                        e.stopPropagation(); // Evitar que se active la descarga
                                        handleViewFile(file);
                                    }}
                                    title="Ver archivo"
                                >
                                    👁️
                                </button>
                                <button
                                    className="action-btn download-btn"
                                    onClick={(e) => {
                                        e.stopPropagation(); // Evitar doble descarga
                                        handleDownloadFile(file);
                                    }}
                                    title="Descargar archivo"
                                >
                                    ⬇️
                                </button>
                                {puedeSubir && (
                                    <button
                                        className="action-btn rename-btn"
                                        onClick={(e) => {
                                            e.stopPropagation(); // Evitar que se active la descarga
                                            handleRenameFile(file);
                                        }}
                                        title="Renombrar archivo"
                                    >
                                        ✏️
                                    </button>
                                )}
                                {puedeEliminar && (
                                    <button
                                        className="action-btn delete-btn"
                                        onClick={(e) => {
                                            e.stopPropagation(); // Evitar que se active la descarga
                                            handleDeleteFile(file);
                                        }}
                                        title="Eliminar archivo"
                                    >
                                        🗑️
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                ))}
            </div>
            {totalPages > 1 && (
                <div className="pagination-container">
                    <div className="pagination">
                        <button
                            className="pagination-btn"
                            onClick={previousPage}
                            disabled={currentPage === 0}
                        >
                            ← Anterior
                        </button>

                        <div className="pagination-info">
                            {Array.from({ length: totalPages }, (_, i) => (
                                <button
                                    key={i}
                                    className={`pagination-number ${currentPage === i ? 'active' : ''}`}
                                    onClick={() => goToPage(i)}
                                >
                                    {i + 1}
                                </button>
                            ))}
                        </div>

                        <button
                            className="pagination-btn"
                            onClick={nextPage}
                            disabled={currentPage === totalPages - 1}
                        >
                            Siguiente →
                        </button>
                    </div>
                    <div className="pagination-summary">
                        Mostrando {currentPage * pageSize + 1}-{Math.min((currentPage + 1) * pageSize, filteredAndSortedFiles.length)} de {filteredAndSortedFiles.length} archivos
                    </div>
                </div>
            )}
        </div>
    );

    const renderListView = () => (
        <div>
            <div className="files-list">
                <table className="files-table">
                    <thead>
                        {table.getHeaderGroups().map(headerGroup => (
                            <tr key={headerGroup.id} className="files-list-header">
                                {headerGroup.headers.map(header => (
                                    <th
                                        key={header.id}
                                        className={`header-cell ${header.column.getCanSort() ? 'sortable' : ''}`}
                                        style={{ width: header.getSize() }}
                                        onClick={header.column.getToggleSortingHandler()}
                                    >
                                        {flexRender(header.column.columnDef.header, header.getContext())}
                                        {{
                                            asc: ' ↑',
                                            desc: ' ↓',
                                        }[header.column.getIsSorted()] ?? null}
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody>
                        {table.getRowModel().rows.map(row => (
                            <tr key={row.id} className="file-list-item">
                                {row.getVisibleCells().map(cell => (
                                    <td key={cell.id}>
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Paginación para vista de lista */}
            <div className="pagination-container">
                <div className="pagination">
                    <button
                        className="pagination-btn"
                        onClick={() => table.previousPage()}
                        disabled={!table.getCanPreviousPage()}
                    >
                        ← Anterior
                    </button>

                    <div className="pagination-info">
                        {Array.from({ length: table.getPageCount() }, (_, i) => (
                            <button
                                key={i}
                                className={`pagination-number ${table.getState().pagination.pageIndex === i ? 'active' : ''}`}
                                onClick={() => table.setPageIndex(i)}
                            >
                                {i + 1}
                            </button>
                        ))}
                    </div>

                    <button
                        className="pagination-btn"
                        onClick={() => table.nextPage()}
                        disabled={!table.getCanNextPage()}
                    >
                        Siguiente →
                    </button>
                </div>
                <div className="pagination-summary">
                    Mostrando {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1}-{Math.min((table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize, table.getPrePaginationRowModel().rows.length)} de {table.getPrePaginationRowModel().rows.length} archivos
                </div>
            </div>
        </div>
    );

    // ========================================
    // RENDERIZADO PRINCIPAL
    // ========================================
    if (loading) {
        return (
            <div className="loading-spinner">
                Cargando archivos...
            </div>
        );
    }

    return (
        <div>
            {/* Zona de subida: solo si el usuario tiene permiso para subir archivos */}
            {puedeSubir && (
                <div
                    className={`drag-drop-area ${dragOver ? 'drag-over' : ''}`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                >
                    <div className="drag-drop-text">
                        {uploading ? (
                            <>
                                <div className="loading-spinner"></div>
                                Subiendo archivos...
                            </>
                        ) : (
                            <>
                                Arrastra archivos aquí o
                                <button
                                    className="upload-button"
                                    onClick={() => fileInputRef.current?.click()}
                                    style={{ marginLeft: '8px' }}
                                >
                                    📎 Seleccionar archivos
                                </button>
                            </>
                        )}
                    </div>

                    <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        onChange={handleFileSelect}
                        className="upload-input"
                    />
                </div>
            )}

            {files.length === 0 ? (
                <div className="empty-state">
                    <div className="empty-state-icon">📄</div>
                    <div className="empty-state-text">
                        {puedeSubir
                            ? 'Esta carpeta está vacía. Sube algunos archivos para comenzar.'
                            : 'Esta carpeta está vacía.'}
                    </div>
                </div>
            ) : (
                <>
                    {renderControls()}
                    {viewMode === 'grid' ? renderGridView() : renderListView()}
                </>
            )}

            {/* Modal para pedir fecha del archivo antes de subir */}
            {showDateModal && (
                <div className="modal-overlay2" onClick={handleCancelUpload}>
                    <div className="modal2" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header2">
                            <h2 className="modal-title2">Fecha del archivo</h2>
                        </div>
                        <div className="modal-body2">
                            <p>¿Qué fecha quieres asignar a {pendingFiles.length === 1 ? 'este archivo' : `estos ${pendingFiles.length} archivos`}?</p>

                            <div className="form-group2">
                                <label className="form-label2">Fecha del archivo:</label>
                                <input
                                    type="date"
                                    className="form-input2"
                                    value={tempFechaArchivo}
                                    onChange={(e) => setTempFechaArchivo(e.target.value)}
                                    autoFocus
                                />
                            </div>

                            {pendingFiles.length > 0 && (
                                <div className="files-preview2">
                                    <strong>Archivos a subir:</strong>
                                    <ul style={{ marginTop: '10px', paddingLeft: '20px' }}>
                                        {pendingFiles.map((file, index) => (
                                            <li key={index} style={{ marginBottom: '5px', fontSize: '14px' }}>
                                                {file.name}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                        <div className="modal-actions2">
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={handleCancelUpload}
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={handleConfirmDateAndUpload}
                                disabled={!tempFechaArchivo}
                            >
                                Subir {pendingFiles.length === 1 ? 'archivo' : 'archivos'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FilesView;
